import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Field, Input, Select } from '../ui';
import { api, ApiError } from '../../lib/api';
import type { CanonicalNutrient, NutrientMapping } from '../../lib/types';

export function OrgNutrientMappings({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const [effective, setEffective] = useState<NutrientMapping[]>([]);
  const [orgMappings, setOrgMappings] = useState<NutrientMapping[]>([]);
  const [canonical, setCanonical] = useState<CanonicalNutrient[]>([]);
  const [filter, setFilter] = useState('');
  const [showOrgOnly, setShowOrgOnly] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'green' | 'red'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<NutrientMapping | null>(null);
  const [newForm, setNewForm] = useState({
    alias: '',
    canonicalNutrient: '',
    factor: '1.0',
    notes: '',
  });

  const base = `/api/v1/orgs/${orgId}/nutrients/mappings`;

  const load = useCallback(async () => {
    try {
      const r = await api<{
        orgMappings: NutrientMapping[];
        effectiveMappings: NutrientMapping[];
        canonicalNutrients: CanonicalNutrient[];
      }>(base);
      setOrgMappings(r.orgMappings);
      setEffective(r.effectiveMappings);
      setCanonical(r.canonicalNutrients);
      if (!newForm.canonicalNutrient && r.canonicalNutrients[0]) {
        setNewForm((f) => ({ ...f, canonicalNutrient: r.canonicalNutrients[0]!.name }));
      }
    } catch (e) {
      setMsg({ tone: 'red', text: e instanceof ApiError ? e.message : String(e) });
    }
  }, [base, newForm.canonicalNutrient]);

  useEffect(() => {
    void load();
  }, [load]);

  const addMapping = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api(base, {
        method: 'POST',
        json: {
          alias: newForm.alias,
          canonicalNutrient: newForm.canonicalNutrient,
          factor: Number(newForm.factor) || 1.0,
          notes: newForm.notes || null,
        },
      });
      setMsg({ tone: 'green', text: t('nutrients.added', 'Org custom mapping added successfully') });
      setShowAdd(false);
      setNewForm((f) => ({ ...f, alias: '', factor: '1.0', notes: '' }));
      await load();
    } catch (e) {
      setMsg({ tone: 'red', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    setMsg(null);
    try {
      await api(`${base}/${editing.id}`, {
        method: 'PATCH',
        json: {
          alias: editing.alias,
          canonicalNutrient: editing.canonicalNutrient,
          factor: Number(editing.factor) || 1.0,
          notes: editing.notes || null,
        },
      });
      setMsg({ tone: 'green', text: t('nutrients.updated', 'Mapping updated successfully') });
      setEditing(null);
      await load();
    } catch (e) {
      setMsg({ tone: 'red', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const removeMapping = async (id: string, alias: string) => {
    if (!window.confirm(t('nutrients.confirmDelete', { alias, defaultValue: `Delete mapping "${alias}"?` })))
      return;
    setBusy(true);
    setMsg(null);
    try {
      await api(`${base}/${id}`, { method: 'DELETE' });
      setMsg({ tone: 'green', text: t('nutrients.deleted', 'Mapping deleted') });
      await load();
    } catch (e) {
      setMsg({ tone: 'red', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const displayed = (showOrgOnly ? orgMappings : effective).filter(
    (m) =>
      m.alias.toLowerCase().includes(filter.toLowerCase()) ||
      m.canonicalNutrient.toLowerCase().includes(filter.toLowerCase()) ||
      (m.notes ?? '').toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            {t('nutrients.orgTitle', 'Organization Nutrient Mappings')}
          </h2>
          <p className="text-xs text-slate-500">
            {t(
              'nutrients.orgSubtitle',
              'Custom aliases and compound-to-elemental conversion factors for your organization.',
            )}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canEdit && (
            <Button onClick={() => setShowAdd((v) => !v)}>
              {showAdd ? t('app.cancel') : t('nutrients.addCustom', '+ Add Custom Mapping')}
            </Button>
          )}
        </div>
      </div>

      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}

      {showAdd && (
        <Card title={t('nutrients.addCustom', 'Add Custom Org Mapping')}>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            <Field label={`${t('nutrients.alias', 'Ingredient / Synonym Alias')} *`}>
              <Input
                value={newForm.alias}
                onChange={(e) => setNewForm({ ...newForm, alias: e.target.value })}
                placeholder="e.g. Acerola Cherry Extract, 유기농비타민C"
              />
            </Field>
            <Field label={`${t('nutrients.canonical', 'Canonical Nutrient (DV)')} *`}>
              <Select
                value={newForm.canonicalNutrient}
                onChange={(e) => setNewForm({ ...newForm, canonicalNutrient: e.target.value })}
              >
                {canonical.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.unit})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('nutrients.factor', 'Elemental Factor (e.g. 0.25)')}>
              <Input
                type="number"
                step="0.001"
                min="0.001"
                max="10"
                value={newForm.factor}
                onChange={(e) => setNewForm({ ...newForm, factor: e.target.value })}
              />
            </Field>
            <Field label={t('nutrients.notes', 'Notes')}>
              <Input
                value={newForm.notes}
                onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })}
                placeholder="e.g. Standardized to 25% Vitamin C"
              />
            </Field>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowAdd(false)}>
              {t('app.cancel')}
            </Button>
            <Button
              onClick={() => void addMapping()}
              disabled={!newForm.alias || !newForm.canonicalNutrient || busy}
            >
              {t('app.save')}
            </Button>
          </div>
        </Card>
      )}

      {editing && (
        <Card title={t('nutrients.editMapping', 'Edit Org Mapping')}>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            <Field label={`${t('nutrients.alias', 'Ingredient / Synonym Alias')} *`}>
              <Input
                value={editing.alias}
                onChange={(e) => setEditing({ ...editing, alias: e.target.value })}
              />
            </Field>
            <Field label={`${t('nutrients.canonical', 'Canonical Nutrient (DV)')} *`}>
              <Select
                value={editing.canonicalNutrient}
                onChange={(e) => setEditing({ ...editing, canonicalNutrient: e.target.value })}
              >
                {canonical.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.unit})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('nutrients.factor', 'Elemental Factor')}>
              <Input
                type="number"
                step="0.001"
                min="0.001"
                max="10"
                value={String(editing.factor)}
                onChange={(e) => setEditing({ ...editing, factor: Number(e.target.value) || 1.0 })}
              />
            </Field>
            <Field label={t('nutrients.notes', 'Notes')}>
              <Input
                value={editing.notes ?? ''}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
            </Field>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {t('app.cancel')}
            </Button>
            <Button
              onClick={() => void saveEdit()}
              disabled={!editing.alias || !editing.canonicalNutrient || busy}
            >
              {t('app.save')}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('nutrients.searchPlaceholder', 'Filter by alias or nutrient...')}
            className="max-w-md"
          />
          <div className="flex items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={showOrgOnly}
                onChange={(e) => setShowOrgOnly(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="font-medium text-slate-700">
                {t('nutrients.showOrgOnly', {
                  count: orgMappings.length,
                  defaultValue: `Org Custom Only (${orgMappings.length})`,
                })}
              </span>
            </label>
          </div>
          <span className="ml-auto text-xs text-slate-500">
            {displayed.length} {t('nutrients.shown', 'shown')}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2.5 px-3">{t('nutrients.alias', 'Alias / Synonym')}</th>
                <th className="py-2.5 px-3">{t('nutrients.canonical', 'Canonical Nutrient')}</th>
                <th className="py-2.5 px-3 text-right">{t('nutrients.factor', 'Factor')}</th>
                <th className="py-2.5 px-3">{t('nutrients.source', 'Source')}</th>
                <th className="py-2.5 px-3">{t('nutrients.notes', 'Notes')}</th>
                {canEdit && <th className="py-2.5 px-3 text-right">{t('app.actions', 'Actions')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayed.map((m) => (
                <tr key={`${m.source}-${m.id}`} className="hover:bg-slate-50">
                  <td className="py-2 px-3 font-medium text-slate-900">{m.alias}</td>
                  <td className="py-2 px-3">
                    <Badge tone="green">{m.canonicalNutrient}</Badge>
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-xs">
                    {m.factor !== 1.0 ? m.factor.toFixed(3) : '1.0'}
                  </td>
                  <td className="py-2 px-3">
                    <Badge tone={m.source === 'org' ? 'blue' : 'slate'}>
                      {m.source === 'org'
                        ? t('nutrients.orgSource', 'Org Custom')
                        : t('nutrients.defaultSource', 'Default')}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-500">{m.notes || '-'}</td>
                  {canEdit && (
                    <td className="py-2 px-3 text-right">
                      {m.source === 'org' && (
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing(m)}>
                            {t('app.edit')}
                          </Button>
                          <Button
                            variant="ghost"
                            className="px-2 py-1 text-xs text-red-600 hover:text-red-700"
                            onClick={() => void removeMapping(m.id, m.alias)}
                          >
                            {t('app.delete')}
                          </Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {displayed.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 6 : 5} className="py-8 text-center text-slate-500">
                    {t('app.noResults', 'No nutrient mappings found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
